import {
  isModifierCompletelyInactiveAcrossSelection,
  modifierBlocksEditActivation,
  modifierHasInvisibleActivePriceState,
  modifierHasPriceLockRelevantState,
} from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierDisplay.helpers';
import { Modifier } from '@/types/menus.types';
import { useMemo } from 'react';
import { TFunction } from 'i18next';

type ModifierActionButtonState = {
  isDeactivateButtonDisabled: boolean;
  shouldShowDeactivateTooltip: boolean;
  deactivateTooltipMessage: string;
  isEditPriceButtonDisabled: boolean;
  shouldShowEditPriceTooltip: boolean;
  editPriceTooltipMessage: string;
};

function hasMixedZeroPricedSelection(modifiers: Modifier[]): boolean {
  if (modifiers.length < 2) return false;
  const zeroPricedCount = modifiers.filter((modifier) => modifier.isZeroPriced === true).length;
  return zeroPricedCount > 0 && zeroPricedCount < modifiers.length;
}

/** Inactive rows block Deactivate unless they are zero-priced (often Inactive until first activation). */
function hasDeactivateBlockingInactiveModifier(modifiers: Modifier[], selectedStoreGroupNames: string[]): boolean {
  return modifiers.some(
    (modifier) =>
      modifier.isZeroPriced !== true && isModifierCompletelyInactiveAcrossSelection(modifier, selectedStoreGroupNames),
  );
}

export function useModifierActionButtonState(
  selectedModifiers: Modifier[],
  selectedRowCount: number,
  selectedStoreGroupNames: string[],
  t: TFunction,
): ModifierActionButtonState {
  return useMemo(() => {
    const hasSelection = selectedRowCount > 0;
    const mixedZeroPriced = hasMixedZeroPricedSelection(selectedModifiers);
    const priceLockedForEdit = selectedModifiers.some(modifierHasPriceLockRelevantState);
    const invisibleActivePriceForEdit = selectedModifiers.some(modifierHasInvisibleActivePriceState);
    const deactivateBlockingInactive = hasDeactivateBlockingInactiveModifier(
      selectedModifiers,
      selectedStoreGroupNames,
    );
    const mandatorySelected = selectedModifiers.some((modifier) => modifier.isMandatory === true);

    const isDeactivateButtonDisabled = !hasSelection || mandatorySelected || deactivateBlockingInactive;
    const shouldShowDeactivateTooltip = mandatorySelected || deactivateBlockingInactive;
    const deactivateTooltipMessage = mandatorySelected
      ? t('deactivateModifiers.mandatoryModifiersSelectedTooltip')
      : t('deactivateModifiers.inactiveModifiersSelectedTooltip');

    const editBlocked = mixedZeroPriced || selectedModifiers.some(modifierBlocksEditActivation);
    const isEditPriceButtonDisabled = !hasSelection || editBlocked;

    let editPriceTooltipMessage = '';
    if (mixedZeroPriced) {
      editPriceTooltipMessage = t('menu.tooltips.editModifierPriceMixedZeroPriced');
    } else if (priceLockedForEdit) {
      editPriceTooltipMessage = t('menu.tooltips.editModifierPricePriceLock');
    } else if (invisibleActivePriceForEdit) {
      editPriceTooltipMessage = t('menu.tooltips.editModifierPriceInvisibleActive');
    }

    return {
      isDeactivateButtonDisabled,
      shouldShowDeactivateTooltip,
      deactivateTooltipMessage,
      isEditPriceButtonDisabled,
      shouldShowEditPriceTooltip: hasSelection && editBlocked,
      editPriceTooltipMessage,
    };
  }, [selectedModifiers, selectedRowCount, selectedStoreGroupNames, t]);
}
